import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaPaperPlane } from 'react-icons/fa';
import {
  PHONE_DISPLAY,
  PHONE_HREF,
  SALES_PHONE_DISPLAY,
  SALES_PHONE_HREF,
  SALES_PHONE_LABEL,
} from '../../constants/contactInfo.js';
import { serviceRequestOptions } from './servicesData.js';
import { getApiBaseUrl } from '../../utils/api.js';

const SERVICE_REQUEST_EMAIL = 'site@samvdpo.ru';

const initialFormState = {
  name: '',
  phone: '',
  email: '',
  organization: '',
  service: '',
  message: '',
  agreement: false,
  officialCommunicationAcknowledgement: false,
  website: '',
};

const initialTouchedState = {
  name: false,
  phone: false,
  email: false,
  organization: false,
  service: false,
  message: false,
  agreement: false,
  officialCommunicationAcknowledgement: false,
};

function validateForm(values, selectedService) {
  const errors = {};
  const normalizedName = values.name.trim();
  const normalizedPhone = values.phone.replace(/[^\d+]/g, '');
  const normalizedEmail = values.email.trim();
  const normalizedService = (selectedService || values.service).trim();
  const normalizedMessage = values.message.trim();

  if (normalizedName.length < 2) {
    errors.name = 'Укажите имя не короче 2 символов.';
  }

  if (normalizedPhone.length < 6) {
    errors.phone = 'Укажите корректный номер телефона.';
  }

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(normalizedEmail)) {
    errors.email = 'Укажите корректный адрес электронной почты.';
  }

  if (!selectedService && !normalizedService) {
    errors.service = 'Выберите интересующую услугу.';
  }

  if (normalizedMessage.length < 10) {
    errors.message = 'Опишите задачу не короче 10 символов.';
  }

  if (!values.agreement) {
    errors.agreement =
      'Нужно подтвердить согласие на обработку персональных данных.';
  }

  if (!values.officialCommunicationAcknowledgement) {
    errors.officialCommunicationAcknowledgement =
      'Нужно подтвердить, что форма не является официальным каналом подачи обращений.';
  }

  return errors;
}

async function parseResponse(response) {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();

  return { message: text };
}

function ServiceRequestForm({ selectedService = '', nextServicePath = '' }) {
  const [formData, setFormData] = useState({
    ...initialFormState,
    service: selectedService,
  });
  const [touched, setTouched] = useState(initialTouchedState);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  useEffect(() => {
    setFormData((current) => ({
      ...current,
      service: selectedService || current.service,
    }));
  }, [selectedService]);

  function handleChange(event) {
    const { name, value, type, checked } = event.target;
    const nextValue = type === 'checkbox' ? checked : value;

    setFormData((current) => {
      const nextFormData = {
        ...current,
        [name]: nextValue,
      };

      if (touched[name] || errors[name]) {
        setErrors(validateForm(nextFormData, selectedService));
      }

      return nextFormData;
    });
  }

  function handleBlur(event) {
    const { name } = event.target;

    setTouched((current) => ({
      ...current,
      [name]: true,
    }));

    setErrors((currentErrors) => ({
      ...currentErrors,
      ...validateForm(formData, selectedService),
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (formData.website) {
      return;
    }

    const nextTouched = Object.keys(initialTouchedState).reduce(
      (acc, key) => ({
        ...acc,
        [key]: true,
      }),
      {}
    );
    const nextErrors = validateForm(formData, selectedService);

    setTouched(nextTouched);
    setErrors(nextErrors);
    setStatus({ type: '', message: '' });

    if (Object.keys(nextErrors).length > 0) {
      setStatus({
        type: 'error',
        message: 'Проверьте заполнение формы и исправьте отмеченные поля.',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/contact-requests`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: formData.name.trim(),
            phone: formData.phone.trim(),
            email: formData.email.trim(),
            organization: formData.organization.trim(),
            service: (selectedService || formData.service).trim(),
            message: formData.message.trim(),
            consent: formData.agreement,
            officialCommunicationAcknowledgement:
              formData.officialCommunicationAcknowledgement,
          }),
        }
      );

      const data = await parseResponse(response);

      if (!response.ok) {
        throw new Error(
          data.message
          || 'Не удалось отправить заявку. Проверьте настройки формы и попробуйте снова.'
        );
      }

      setStatus({
        type: 'success',
        message:
          'Заявка отправлена. Мы свяжемся с вами после обработки обращения.',
      });
      setFormData({
        ...initialFormState,
        service: selectedService || '',
      });
      setTouched(initialTouchedState);
      setErrors({});
    } catch (error) {
      setStatus({
        type: 'error',
        message:
          error.message ||
          'Не удалось отправить заявку. Попробуйте позже или свяжитесь с нами по телефону.',
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section className="serviceRequestForm" id="service-request-form">
      <div className="serviceRequestForm__intro">
        <p className="serviceRequestForm__kicker">Обратная связь</p>
        <h3 className="serviceRequestForm__title">
          Оставить заявку на консультацию или расчет
        </h3>
        <p className="serviceRequestForm__text">
          Заполните форму, и обращение будет направлено на электронную почту{' '}
          {SERVICE_REQUEST_EMAIL}. Мы используем указанные данные только для
          обработки заявки и обратной связи по вашему запросу.
        </p>
        <p className="serviceRequestForm__text">
          Можно позвонить:{' '}
          <a href={PHONE_HREF} className="serviceRequestForm__phone">
            <span className="serviceRequestForm__phone-number">{PHONE_DISPLAY}</span>
          </a>.{' '}
          <a href={SALES_PHONE_HREF} className="serviceRequestForm__phone">
            {SALES_PHONE_LABEL}:{' '}
            <span className="serviceRequestForm__phone-number">{SALES_PHONE_DISPLAY}</span>
          </a>.
        </p>
      </div>

      <form
        className="serviceRequestForm__form"
        onSubmit={handleSubmit}
        noValidate
      >
        <div className="serviceRequestForm__grid">
          <label className="serviceRequestForm__field">
            <span className="serviceRequestForm__label">Имя и фамилия</span>
            <input
              type="text"
              name="name"
              className={`serviceRequestForm__input${errors.name && touched.name ? ' serviceRequestForm__input_invalid' : ''}`}
              placeholder="Как к вам обращаться"
              autoComplete="name"
              aria-invalid={Boolean(errors.name && touched.name)}
              value={formData.name}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            {errors.name && touched.name ? (
              <span className="serviceRequestForm__error">{errors.name}</span>
            ) : null}
          </label>

          <label className="serviceRequestForm__field">
            <span className="serviceRequestForm__label">Телефон</span>
            <input
              type="tel"
              name="phone"
              className={`serviceRequestForm__input${errors.phone && touched.phone ? ' serviceRequestForm__input_invalid' : ''}`}
              placeholder="+7 (___) ___-__-__"
              autoComplete="tel"
              aria-invalid={Boolean(errors.phone && touched.phone)}
              value={formData.phone}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            {errors.phone && touched.phone ? (
              <span className="serviceRequestForm__error">{errors.phone}</span>
            ) : null}
          </label>

          <label className="serviceRequestForm__field">
            <span className="serviceRequestForm__label">Электронная почта</span>
            <input
              type="email"
              name="email"
              className={`serviceRequestForm__input${errors.email && touched.email ? ' serviceRequestForm__input_invalid' : ''}`}
              placeholder="example@company.ru"
              autoComplete="email"
              aria-invalid={Boolean(errors.email && touched.email)}
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            {errors.email && touched.email ? (
              <span className="serviceRequestForm__error">{errors.email}</span>
            ) : null}
          </label>

          <label className="serviceRequestForm__field">
            <span className="serviceRequestForm__label">Организация</span>
            <input
              type="text"
              name="organization"
              className="serviceRequestForm__input"
              placeholder="Название организации"
              autoComplete="organization"
              maxLength="160"
              value={formData.organization}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </label>

          <label className="serviceRequestForm__field serviceRequestForm__field_full">
            <span className="serviceRequestForm__label">Интересующая услуга</span>
            {selectedService ? (
              <input
                type="text"
                name="service"
                className="serviceRequestForm__input serviceRequestForm__input_readonly"
                value={selectedService}
                readOnly
              />
            ) : (
              <select
                name="service"
                className={`serviceRequestForm__input${errors.service && touched.service ? ' serviceRequestForm__input_invalid' : ''}`}
                aria-invalid={Boolean(errors.service && touched.service)}
                value={formData.service}
                onChange={handleChange}
                onBlur={handleBlur}
              >
                <option value="">Выберите услугу</option>
                {serviceRequestOptions.map((serviceTitle) => (
                  <option key={serviceTitle} value={serviceTitle}>
                    {serviceTitle}
                  </option>
                ))}
              </select>
            )}
            {errors.service && touched.service ? (
              <span className="serviceRequestForm__error">{errors.service}</span>
            ) : null}
          </label>

          <label className="serviceRequestForm__field serviceRequestForm__field_full">
            <span className="serviceRequestForm__label">Комментарий</span>
            <textarea
              name="message"
              className={`serviceRequestForm__input serviceRequestForm__textarea${errors.message && touched.message ? ' serviceRequestForm__input_invalid' : ''}`}
              placeholder="Опишите задачу, объект, желаемые сроки и любые важные детали"
              aria-invalid={Boolean(errors.message && touched.message)}
              value={formData.message}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            {errors.message && touched.message ? (
              <span className="serviceRequestForm__error">{errors.message}</span>
            ) : null}
          </label>

          <label className="serviceRequestForm__trap" aria-hidden="true">
            <span>Сайт</span>
            <input
              type="text"
              name="website"
              tabIndex="-1"
              autoComplete="off"
              value={formData.website}
              onChange={handleChange}
            />
          </label>
        </div>

        <div className="serviceRequestForm__consent">
          <label className="serviceRequestForm__consent-label">
            <input
              type="checkbox"
              name="agreement"
              className="serviceRequestForm__checkbox"
              checked={formData.agreement}
              onChange={handleChange}
              onBlur={handleBlur}
              aria-invalid={Boolean(errors.agreement && touched.agreement)}
            />
            <span className="serviceRequestForm__consent-text">
              Я подтверждаю, что ознакомлен(а) с{' '}
              <Link to="/privacy-policy/" className="serviceRequestForm__link">
                политикой обработки персональных данных
              </Link>
              ,{' '}
              <Link
                to="/personal-data-consent/"
                className="serviceRequestForm__link"
              >
                согласием на обработку персональных данных
              </Link>{' '}
              и даю согласие на обработку указанных в форме данных для
              рассмотрения обращения и обратной связи.
            </span>
          </label>
          {errors.agreement && touched.agreement ? (
            <p className="serviceRequestForm__error">{errors.agreement}</p>
          ) : null}
          <p className="serviceRequestForm__notice">
            Не указывайте в сообщении специальные категории персональных данных,
            платежные реквизиты, пароли и иную избыточную конфиденциальную
            информацию.
          </p>
          <label className="serviceRequestForm__consent-label">
            <input
              type="checkbox"
              name="officialCommunicationAcknowledgement"
              className="serviceRequestForm__checkbox"
              checked={formData.officialCommunicationAcknowledgement}
              onChange={handleChange}
              onBlur={handleBlur}
              aria-invalid={Boolean(
                errors.officialCommunicationAcknowledgement
                && touched.officialCommunicationAcknowledgement
              )}
            />
            <span className="serviceRequestForm__consent-text">
              Я подтверждаю, что понимаю: форма обратной связи на сайте
              предназначена для направления запросов о предоставлении услуг и не
              является официальным каналом подачи обращений, заявлений, жалоб и
              иных юридически значимых сообщений.
            </span>
          </label>
          {errors.officialCommunicationAcknowledgement
            && touched.officialCommunicationAcknowledgement ? (
              <p className="serviceRequestForm__error">
                {errors.officialCommunicationAcknowledgement}
              </p>
            ) : null}
          <p className="serviceRequestForm__notice">
            Если обращение связано с подключением, обслуживанием или получением
            информации о системе «Стрелец-Мониторинг», рекомендуем ознакомиться
            с профильным разделом сайта:{' '}
            <Link to="/strelets-monitoring/" className="serviceRequestForm__link">
              Стрелец-Мониторинг
            </Link>
            .
          </p>
        </div>

        <div className="serviceRequestForm__actions">
          <button
            type="submit"
            className="serviceRequestForm__submit"
            disabled={isSubmitting}
          >
            <FaPaperPlane />
            {isSubmitting ? 'Отправка...' : 'Отправить заявку'}
          </button>

          {nextServicePath ? (
            <Link
              to={nextServicePath}
              className="serviceRequestForm__secondaryLink"
            >
              Следующая услуга
            </Link>
          ) : null}
        </div>

        {status.message ? (
          <p
            className={`serviceRequestForm__status serviceRequestForm__status_${status.type}`}
          >
            {status.type === 'success' ? <FaCheckCircle /> : null}
            <span>{status.message}</span>
          </p>
        ) : null}
      </form>
    </section>
  );
}

export default ServiceRequestForm;
